import { type Kind, type LocalizationMap } from './content.js';
import { type Intent } from './intent.js';
import { INTENT_PARAM, PARAMS_BY_KIND } from './params.js';
import { type SchemeFull } from './scheme.js';

const REGEX_STATIC = /{{(\w+?)}}/gm;
const REGEX_INTENT = /{{(\w+):([\s\S]+?)}}/gm;

type IsValidArgs = {
  template: string;
  intents: readonly Intent[];
} & (
  | { allowCustomVariables: true; validVariables?: readonly string[] }
  | { allowCustomVariables?: false; validVariables: readonly string[] }
);

export function isValid(args: IsValidArgs): string | undefined {
  if (!args.allowCustomVariables) {
    const invalidStatic = findInvalidStatic({
      template: args.template,
      validVariables: args.validVariables,
    });
    if (invalidStatic) {
      return invalidStatic;
    }
  }
  const invalidIntent = findInvalidIntents(args);
  if (invalidIntent) {
    return invalidIntent;
  }
  return undefined;
}

export function findCustomVariables(args: {
  template: string;
  validVariables: readonly string[];
}): string[] {
  const { template, validVariables } = args;
  const customVariables: string[] = [];
  for (const key of extractStaticKeys(template)) {
    if (!validVariables.includes(key) && !customVariables.includes(key)) {
      customVariables.push(key);
    }
  }
  return customVariables;
}

export function findContentCustomVariables(args: {
  localizations: LocalizationMap;
  kind: Kind;
}): string[] {
  const { localizations, kind } = args;
  const validVariables = PARAMS_BY_KIND[kind];
  const customVariables = new Set<string>();
  for (const { content, subject } of Object.values(localizations)) {
    for (const template of [content, subject]) {
      if (!template) {
        continue;
      }
      for (const variable of findCustomVariables({ template, validVariables })) {
        customVariables.add(variable);
      }
    }
  }
  return [...customVariables];
}

export function findSchemeCustomVariables(scheme: SchemeFull | undefined): string[] {
  if (!scheme) {
    return [];
  }
  const customVariables = new Set<string>();
  for (const config of scheme.configs) {
    for (const variable of findContentCustomVariables({
      localizations: config.content.localizations,
      kind: config.content.kind,
    })) {
      customVariables.add(variable);
    }
  }
  return [...customVariables].sort();
}

function findInvalidStatic(args: {
  template: string;
  validVariables: readonly string[];
}): string | undefined {
  const { template, validVariables } = args;
  return extractStaticKeys(template).find((key) => !validVariables.includes(key));
}

function extractStaticKeys(template: string): string[] {
  return [...template.matchAll(REGEX_STATIC)].map((match) => match[1]);
}

function findInvalidIntents(args: {
  template: string;
  intents: readonly Intent[];
}): string | undefined {
  const { template, intents } = args;
  const validIntentIds = intents.map(({ id }) => id);
  const matches = template.matchAll(REGEX_INTENT);
  const htmlKeys: string[] = [];
  const htmlIds: string[] = [];
  for (const match of matches) {
    htmlKeys.push(match[1]);
    htmlIds.push(match[2]);
  }
  for (let i = 0; i < htmlKeys.length; i++) {
    if (htmlKeys[i] !== INTENT_PARAM || !validIntentIds.includes(htmlIds[i])) {
      return `${htmlKeys[i]}:${htmlIds[i]}`;
    }
  }
  return undefined;
}
